import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { unwrap } from '@/lib/result';
import { useSessionStore } from '@/stores/session.store';

import { masjidRepository } from '../api/masjid.repository';
import type { MasjidCreateInput } from '../model/masjid';

export const masjidKeys = {
  all: ['masjid'] as const,
  mine: (userId: string) => [...masjidKeys.all, 'managed', userId] as const,
  members: (masjidId: string) => [...masjidKeys.all, 'members', masjidId] as const,
};

/** Masjids the signed-in user owns or co-manages (Phase 4). */
export function useMyMasjids() {
  const userId = useSessionStore((s) => s.userId);
  return useQuery({
    queryKey: masjidKeys.mine(userId ?? 'anon'),
    queryFn: () => unwrap(masjidRepository.listManaged()),
    enabled: Boolean(userId),
  });
}

export function useCreateMasjid() {
  const queryClient = useQueryClient();
  const userId = useSessionStore((s) => s.userId);
  return useMutation({
    mutationFn: (input: Omit<MasjidCreateInput, 'adminId'>) =>
      unwrap(masjidRepository.create({ ...input, adminId: userId as string })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: masjidKeys.mine(userId ?? 'anon') });
    },
  });
}

export function useMasjidMembers(masjidId: string) {
  return useQuery({
    queryKey: masjidKeys.members(masjidId),
    queryFn: () => unwrap(masjidRepository.listMembers(masjidId)),
    enabled: Boolean(masjidId),
  });
}

export function useAddMasjidMember(masjidId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (phone: string) => unwrap(masjidRepository.addMemberByPhone(masjidId, phone)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: masjidKeys.members(masjidId) });
    },
  });
}

export function useRemoveMasjidMember(masjidId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => unwrap(masjidRepository.removeMember(memberId)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: masjidKeys.members(masjidId) });
    },
  });
}
